"use client";

import {FormEvent, useCallback, useMemo, useState} from "react";
import {Form} from "react-aria-components";
import {TextFieldClasses} from "../../../../_components/forms/TextField";
import Button from "../../../../_components/Button";
import AlertBox, {AlertType} from "@/app/_components/AlertBox";
import Campus from "@/app/types/Campus";
import RoleRestrictedView from "@/app/_components/layout/RoleRestrictedView";
import UninitializedParentInputFields from "./UninitializedParentInputFields";
import ContactFormCampusSelectors from "./ContactFormCampusSelectors";
import StudentFields from "./ContactFormStudentFields";
import useContactForm from "./useContactForm";
import {
	ContactFormContextValues,
	useContactFormContext,
} from "@/app/_components/contexts/ContactFormContext";
import useContactFormParentFields from "./useContactFormParentFields";
import useContactFormSelectedCampuses from "./useContactFormSelectedCampuses";
import useContactFormStudentFields from "./useContactFormStudentFields";
import {STUDENT_ROLES} from "@/app/util/userUtils";
import {StudentNameClasses} from "./AcademyStudentName";
import CheckmarkCheckbox from "@/app/_components/forms/CheckmarkCheckbox";
import {useUserInfo} from "@/app/_components/contexts/userInfoContext";
import ContactFormLoadingPlaceholder from "./ContactFormLoadingPlaceholder";
import ErrorsAlertBox from "@/app/_components/ErrorsAlertBox";

type ContactFormClasses = {
	fields?: TextFieldClasses;
	addStudentButton?: string;
	studentNames?: StudentNameClasses;
};

export type ContactFormProps = {
	campusList?: Campus[];
	classes?: ContactFormClasses;
	successRedirectURL?: string;
};

const ContactForm: React.FC<ContactFormProps> = ({
	campusList = [],
	classes,
	successRedirectURL,
}) => {
	const {isLoading: isLoadingUser} = useUserInfo();

	const {isSubmitting, serverErrors, submitForm} = useContactForm({
		successRedirectURL,
	});

	// call hooks and create a fallback state if context doesn't exist
	const [isOptedIntoEmails, setIsOptedIntoEmails] = useState<boolean>(true);
	const contextFallback: ContactFormContextValues = {
		contactFormUserInfo: {
			isLoadingStudents: false,
		},
		parentFieldControls: useContactFormParentFields(),
		studentFieldControls: useContactFormStudentFields(),
		selectedCampusControls: useContactFormSelectedCampuses(),
		optInControls: {isOptedIntoEmails, setIsOptedIntoEmails},
	};

	const {
		contactFormUserInfo,
		parentFieldControls,
		studentFieldControls,
		selectedCampusControls,
		optInControls,
	} = useContactFormContext() ?? contextFallback;

	const onContactFormSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			submitForm({
				parentFields: parentFieldControls,
				studentFields: studentFieldControls,
				selectedCampuses: selectedCampusControls,
				isOptedIntoEmails: optInControls.isOptedIntoEmails,
			});
		},
		[
			parentFieldControls,
			selectedCampusControls,
			studentFieldControls,
			optInControls,
			submitForm,
		]
	);

	const isLoading = useMemo(() => {
		return isLoadingUser || contactFormUserInfo.isLoadingStudents;
	}, [isLoadingUser, contactFormUserInfo]);

	if (isLoading) {
		return <ContactFormLoadingPlaceholder />;
	}

	return (
		<RoleRestrictedView
			hiddenForRoles={STUDENT_ROLES}
			fallbackElement={
				<AlertBox type={AlertType.ERROR}>
					Please ask your parent to submit the consultation request
				</AlertBox>
			}
		>
			<ErrorsAlertBox errors={serverErrors} />
			<Form
				className="consultationRequestUniversalForm flex max-w-2xl flex-col gap-6"
				onSubmit={onContactFormSubmit}
			>
				<div className="flex flex-col gap-4">
					<UninitializedParentInputFields
						{...parentFieldControls}
						classes={{fields: classes?.fields}}
					/>
					{contactFormUserInfo.studentsByApp && (
						<div>
							<ContactFormCampusSelectors
								campusList={campusList}
								selectorClasses={classes?.fields}
								{...selectedCampusControls}
							/>
						</div>
					)}
					<div>
						<StudentFields
							allStudents={contactFormUserInfo.studentsByApp}
							classes={classes}
							{...studentFieldControls}
						/>
					</div>
					{!contactFormUserInfo.studentsByApp && (
						<div>
							<ContactFormCampusSelectors
								campusList={campusList}
								selectorClasses={classes?.fields}
								{...selectedCampusControls}
							/>
						</div>
					)}
				</div>
				<CheckmarkCheckbox
					id="email-optin-checkbox"
					name="optin"
					isSelected={optInControls.isOptedIntoEmails}
					onChange={optInControls.setIsOptedIntoEmails}
				>
					<p>
						Yes, I would like to receive information about new courses, seasonal
						schedules, and event announcements from AoPS Academy.
					</p>
				</CheckmarkCheckbox>
				<Button
					id="contact-form-submit-button"
					className="md:self-start"
					disabled={isSubmitting}
				>
					SUBMIT
				</Button>
			</Form>
		</RoleRestrictedView>
	);
};

export default ContactForm;
